import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// Reading comfort: how big the page renders, and which typeface it renders in.
// Sibling of useTheme.tsx — same shape, same persistence story, and the same
// no-FOUC bootstrap inline in index.html (keep the storage keys and the
// accepted values here in step with it).
//
// Size is a page zoom rather than a font-size cascade. Every size in this app
// is written in px — across four stylesheets, a hundred inline <style> blocks
// and a hundred more inline style props — so there is no single font-size
// token to turn up. `zoom` on the app root scales the whole rendered box tree
// the way the browser's own zoom does, which is what "make the text bigger"
// actually means to someone reading a recipe on a counter two feet away.
//
// The cost of zoom is that anything measuring the viewport in JS is now
// working in a different coordinate space than the elements it positions. Two
// places do that — the tour spotlight and the recipe MORE menu — and both are
// portalled to <body>, outside the zoomed subtree, so their maths keeps
// working in plain viewport pixels. Anything sized in vh/vw inside the app
// divides by --app-zoom for the same reason.

const SCALE_KEY = "affinage.textScale";
const FONT_KEY = "affinage.fontFace";

/** The steps the +/- buttons walk through. 100% is the design's own scale. */
export const TEXT_SCALES = [0.9, 1, 1.1, 1.25, 1.4] as const;
const DEFAULT_SCALE = 1;

export type FontFace = "editorial" | "modern" | "classic";

/**
 * The three grounds, all built from families already loaded in index.html —
 * picking one costs nothing extra over the network.
 */
export const FONT_FACES: Array<{ id: FontFace; label: string; note: string }> = [
  { id: "editorial", label: "Editorial", note: "Playfair headlines, old-style body" },
  { id: "modern", label: "Modern", note: "Inter throughout" },
  { id: "classic", label: "Classic", note: "Book serif throughout" },
];

type DisplayState = {
  scale: number;
  font: FontFace;
  setScale: (s: number) => void;
  setFont: (f: FontFace) => void;
  /** Step one stop up (+1) or down (-1) the TEXT_SCALES ladder. */
  stepScale: (dir: 1 | -1) => void;
  reset: () => void;
  atMin: boolean;
  atMax: boolean;
};

const DisplayCtx = createContext<DisplayState | null>(null);

function isFontFace(v: unknown): v is FontFace {
  return v === "editorial" || v === "modern" || v === "classic";
}

/** Snap an arbitrary stored number to the nearest rung of the ladder. */
function nearestScale(value: number): number {
  return TEXT_SCALES.reduce((best, s) =>
    Math.abs(s - value) < Math.abs(best - value) ? s : best
  );
}

function readStoredScale(): number | null {
  try {
    const raw = window.localStorage.getItem(SCALE_KEY);
    if (!raw) return null;
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n)) return null;
    return nearestScale(n);
  } catch {
    return null;
  }
}

function readStoredFont(): FontFace | null {
  try {
    const v = window.localStorage.getItem(FONT_KEY);
    return isFontFace(v) ? v : null;
  } catch {
    return null;
  }
}

export function DisplayProvider({ children }: { children: ReactNode }) {
  const [scale, setScaleState] = useState<number>(() => readStoredScale() ?? DEFAULT_SCALE);
  const [font, setFontState] = useState<FontFace>(() => readStoredFont() ?? "editorial");

  useEffect(() => {
    document.documentElement.style.setProperty("--app-zoom", String(scale));
  }, [scale]);

  useEffect(() => {
    document.documentElement.setAttribute("data-font", font);
  }, [font]);

  const setScale = useCallback((s: number) => {
    const next = nearestScale(s);
    try {
      window.localStorage.setItem(SCALE_KEY, String(next));
    } catch {
      // localStorage unavailable; the choice just won't survive a reload
    }
    setScaleState(next);
  }, []);

  const setFont = useCallback((f: FontFace) => {
    try {
      window.localStorage.setItem(FONT_KEY, f);
    } catch {
      // as above
    }
    setFontState(f);
  }, []);

  const stepScale = useCallback(
    (dir: 1 | -1) => {
      const i = TEXT_SCALES.indexOf(nearestScale(scale) as (typeof TEXT_SCALES)[number]);
      const next = TEXT_SCALES[Math.min(TEXT_SCALES.length - 1, Math.max(0, i + dir))];
      if (next !== scale) setScale(next);
    },
    [scale, setScale]
  );

  const reset = useCallback(() => {
    setScale(DEFAULT_SCALE);
    setFont("editorial");
  }, [setScale, setFont]);

  const value = useMemo<DisplayState>(
    () => ({
      scale,
      font,
      setScale,
      setFont,
      stepScale,
      reset,
      atMin: scale <= TEXT_SCALES[0],
      atMax: scale >= TEXT_SCALES[TEXT_SCALES.length - 1],
    }),
    [scale, font, setScale, setFont, stepScale, reset]
  );

  return <DisplayCtx.Provider value={value}>{children}</DisplayCtx.Provider>;
}

/**
 * The zoom currently applied to the app root.
 *
 * For the odd bit of code that measures the viewport and then positions
 * something rendered *inside* a zoomed box: getBoundingClientRect() and
 * window.innerWidth/Height speak plain viewport pixels, while a length set on
 * a zoomed element is multiplied by that zoom on the way to the screen.
 * Dividing the measurement by this puts the two back in the same units.
 */
export function currentAppZoom(): number {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--app-zoom");
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : 1;
  } catch {
    return 1;
  }
}

export function useDisplay() {
  const ctx = useContext(DisplayCtx);
  if (!ctx) throw new Error("useDisplay must be used inside DisplayProvider");
  return ctx;
}
